import { Component, HostListener } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DriverRecordRequestService } from '../../../services/driverrecordrequest.service';
import { GeneralService } from '../../../services/general.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { IWizard } from '../../../interfaces/iwizard';
import { WizardSteps } from '../../../interfaces/wizard-steps';
import { Router } from '@angular/router';
import { TranslatorService } from '../../../services/translator.service';
import { UserProfile } from '../../../interfaces/UserProfile';

@Component({
    selector: 'app-billing-address',
    templateUrl: './billing-address.component.html',
    styleUrls: ['./billing-address.component.scss'],
    standalone: false
})
export class BillingAddressComponent {

     form!: FormGroup;
     public wizard!: IWizard;

     constructor (public service: DriverRecordRequestService,
          public generalService: GeneralService,
          public authenticationService: AuthenticationService,
          public _router: Router,
          public translatorService: TranslatorService) {
          this.wizard = this.service.getWizard();
     }

     stateList: any = [];
     isSaving: boolean = false;
     sameAddress: boolean = false; 
     userProfile?: UserProfile;
     isReorder = false;

     ngOnInit(): void {
          setTimeout(() => this.scrollToTop(), 500);
          this.handleBeforeUnload = this.unloadMessage;

          this.userProfile = this.authenticationService.getUserProfile();

          if (this.service.isDriverRequestInitialized === false && this.service.loadFromLocal() === false) {
               this._router.navigateByUrl('/driver/1');
          }

          // Define inputs with validations 
          this.form = new FormGroup({
               addressLine1: new FormControl(this.service.driverRequest.billingAddress?.addressLine1, [
                    Validators.required,
                    Validators.maxLength(100),
                    Validators.pattern(/^([a-zA-Z0-9 '.\/]*)$/)
               ]),
               addressLine2: new FormControl(this.service.driverRequest.billingAddress?.addressLine2, [
                    Validators.maxLength(100),
                    Validators.pattern(/^([a-zA-Z0-9 '.\/]*)$/)
               ]),
               city: new FormControl(this.service.driverRequest.billingAddress?.city, [
                    Validators.required,
                    Validators.maxLength(30),
                    Validators.pattern(/^([a-zA-Z '\-\&amp;']*)$/)
               ]),
               state: new FormControl(this.service.driverRequest.billingAddress?.state, [
                    Validators.required
               ]),
               zip: new FormControl(this.service.driverRequest.billingAddress?.zip, [
                    Validators.required,
                    Validators.minLength(5),
                    Validators.maxLength(10),
                    Validators.pattern(/(^\d{5}(-\d{4})?$)/)
               ]),
               sameAddress: new FormControl(this.sameAddress, [
                    Validators.required
               ])
          });
          
          const statesPromise = this.generalService.getStatesByCountry('USA');
          statesPromise.then((v: any) => this.loadStates(v));
     }

     get addressLine1() { return this.form!.get('addressLine1'); }
     get addressLine2() { return this.form!.get('addressLine2'); }
     get city() { return this.form!.get('city'); }
     get state() { return this.form!.get('state'); }
     get zip() { return this.form!.get('zip'); }

     ngOnDestroy() {
          this.populateBillingAddress();
     }

     populateBillingAddress() {
          this.service.driverRequest.billingAddress = {
               addressLine1: this.form.get('addressLine1')?.value,
               addressLine2: this.form.get('addressLine2')?.value,
               city: this.form.get('city')?.value,
               state: this.form.get('state')?.value,
               zip: this.form.get('zip')?.value,
               countryId: 1,
               country: 'US'
          };

          this.service.saveToLocal();
     }

     loadStates(value: string[]) {
          this.stateList = value;
     }

     isSameAddressChanged() {
          this.sameAddress = !this.sameAddress;
          this.form.controls['addressLine1'].reset();
          this.form.controls['addressLine2'].reset()
          this.form.controls['city'].reset()
          this.form.controls['state'].reset()
          this.form.controls['zip'].reset();
     }

     moveNext() {
          this.isSaving = true;
          this.service.driverRequest.billingAddressSame = this.sameAddress;
          
          if (!this.service.isLexisNexisOn || this.service.isReorderProcess()) {
               // Skip Lexis Nexis pane and go to payment
               this.populateBillingAddress();
               const paymentPromise = this.service.initiatePayment();
               this.generalService.pageLogging('/driver/8', null, '', '', 'Initiated payment, without Lexis Nexis quiz for email ' + this.service.driverRequest.emailAddress);
               paymentPromise.then((result: any) => this.navigateToPayment(result));
          } else {
               // Go to Lexis Nexis pane
               const next = this.service.continueLinkRoute(8);
               this.generalService.pageLogging('/driver/8', null, '', '', 'Driver getting Lexis Nexis quiz, driver email ' + this.service.driverRequest.emailAddress);
               this._router.navigateByUrl(next);
          }
     }

     navigateToPayment(redirectUrl: string | any) {
          this.handleBeforeUnload = this.noUnloadMessage;
          if (redirectUrl) {
               this.generalService.pageLogging('/driver/8', null, '', '',
                    'Payment initiated. Redirecting ' + this.service.driverRequest.emailAddress + ' to ' + redirectUrl);
               redirectUrl = redirectUrl as string;
               const language = this.translatorService.getActiveLanguage().toUpperCase();
               this.translatorService.backupLanguage();

               if (redirectUrl.startsWith('/')) {
                    this._router.navigateByUrl(redirectUrl);
               } else if (redirectUrl.toLowerCase().includes('checkout') === true) {
                    if (language.toLowerCase() !== 'en') {
                         redirectUrl = redirectUrl + '&lang=' + language.toLowerCase();
                    }
                    this.generalService.pageLogging('/driver/8', null, '', '',
                         'Redirecting driver to payment from billing address, email: ' + this.service.driverRequest.emailAddress);
                    window.location = redirectUrl;
               } else if (redirectUrl.includes('orderFail')) {
                    this.generalService.pageLogging('/driver/8', null, '',
                         'Order failed in driver wizard from billing address, email: ' + this.service.driverRequest.emailAddress, '');
                    this._router.navigateByUrl('/driver/11');
               } else if (redirectUrl.includes('dupLic')) {
                    this.generalService.pageLogging('/driver/8', null, '',
                         'Order failed in driver wizard from billing address due to duplicate license found. Email: ' + this.service.driverRequest.emailAddress, '');
                    window.location = redirectUrl;
               } else {
                    this.generalService.pageLogging('/driver/9', null, '',
                         'Navigating to customer support. Order results inconclusive in driver wizard from billing address, email: ' + this.service.driverRequest.emailAddress, '');
                    this._router.navigateByUrl('/customerSupportInfo');
               }
          } else {
               this.generalService.pageLogging('/driver/8', null, '',
                    'Redirect URL from initiatePayment() is null. Driver email: ' + this.service.driverRequest.emailAddress, '');
               this._router.navigateByUrl('/customerSupportInfo');
          }
     }

     scrollToTop() {
          window.scrollTo(0, 0);
     }

     @HostListener('window:beforeunload', ['$event'])
     handleBeforeUnload(event: any): void { };
          
     unloadMessage($event: { returnValue: string; }) {
          return $event.returnValue = 'Your changes will not be saved';
     }
     noUnloadMessage(): void { }
}
